import React from 'react';
import { X, Globe } from 'lucide-react';
import {
  useFloating,
  useInteractions,
  useDismiss,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal
} from '@floating-ui/react';
import type { Language } from '../translations';
import type { TranslationKeys } from '../translations';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onDemoClick: () => void;
  onSignInClick: () => void;
  onLanguageChange: (lang: Language) => void;
  currentLang: Language;
  translations: TranslationKeys;
}

export function MobileMenu({ 
  isOpen, 
  onClose, 
  onDemoClick,
  onSignInClick,
  onLanguageChange, 
  currentLang,
  translations: t
}: MobileMenuProps) {
  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: onClose
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  const languages = {
    NL: 'Nederlands',
    FR: 'Français',
    EN: 'English'
  };

  return (
    <FloatingPortal>
      {isOpen && (
        <FloatingOverlay
          className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          lockScroll
        >
          <FloatingFocusManager context={context}>
            <div
              ref={refs.setFloating}
              {...getFloatingProps()}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 transition-transform duration-300 ease-out"
              style={{
                transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
                maxHeight: '85vh',
                overflowY: 'auto'
              }}
            >
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-xl font-bold text-gray-900">Menu</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <nav className="space-y-6">
                <a 
                  href="#features" 
                  onClick={onClose}
                  className="block text-lg font-medium text-gray-900 hover:text-jaimy-red transition-colors"
                >
                  {t.nav.features}
                </a>
                <a 
                  href="#solutions"
                  onClick={onClose}
                  className="block text-lg font-medium text-gray-900 hover:text-jaimy-red transition-colors"
                >
                  {t.nav.solutions}
                </a>
                <button
                  onClick={() => {
                    onSignInClick();
                    onClose();
                  }}
                  className="block text-lg font-medium text-gray-900 hover:text-jaimy-red transition-colors text-left w-full"
                >
                  {t.nav.signIn}
                </button>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-3">Language</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(languages).map(([code, name]) => (
                      <button
                        key={code}
                        onClick={() => {
                          onLanguageChange(code as Language);
                          onClose();
                        }}
                        className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                          currentLang === code
                            ? 'border-jaimy-red text-jaimy-red bg-jaimy-red/5'
                            : 'border-gray-200 text-gray-600'
                        } transition-colors`}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={onDemoClick}
                  className="w-full gradient-bg text-white py-4 rounded-xl font-medium hover:opacity-90 transition-opacity mt-6"
                >
                  {t.cta.button}
                </button>
              </nav>
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      )}
    </FloatingPortal>
  );
}